import { defineStore, acceptHMRUpdate } from 'pinia'
import Talk from 'talkjs'
import type { ConversationMessage, PublicConversation, PublicConversationAPIResponse } from '~/utils/chat'

export const useChatStore = defineStore('chat', () => {
  const logger = useCanxLogger()
  const conversations = ref<PublicConversation[]>([])
  const privateConversations = ref<{ conversations: { id: string }[], users: CanxUser[] }>({ conversations: [], users: [] })
  const privateConversationMessageToSend = ref<string>('')
  const messages = ref<ConversationMessage[]>([])
  const signature = ref<string>('')
  const session = ref<Talk.Session>()
  const unreadMessageCount = ref<number>(0)
  const activePublicConversation = ref<PublicConversation | null>(null)
  const publicChatbox = ref<Talk.Chatbox | null>(null)
  const { $pwa } = useNuxtApp()
  const { isMobileOrTablet } = useDevice()

  const buildPrivateConversation = (currentUser: CanxUser, otherUserID: number) => {
    const conv = session.value!.getOrCreateConversation(generatePredictableConversationID(currentUser.id, Number(otherUserID)))
    const otherUser = privateConversations.value.users.find((u: CanxUser) => u.id == otherUserID)
    if (!otherUser) {
      throw new Error('Something went wrong. Couldn\'t find chat.')
    }

    conv.setParticipant(new Talk.User({
      id: currentUser.id,
      name: generateUserName(currentUser),
      email: currentUser.email,
      role: 'mob-pwa',
    }))
    conv.setParticipant(new Talk.User({
      id: otherUserID,
      name: generateUserName(otherUser),
      email: otherUser.email,
      role: 'mob-pwa',
    }))

    return conv
  }

  const getSignature = async () => {
    const { data } = await $apiFetch<string>(`chat_signature`, `/api/user/chat/signature`)

    signature.value = data.value!
  }

  const startSession = async (currentUser: CanxUser, appId: string) => {
    const talkjsUser = {
      id: currentUser.id,
      name: generateUserName(currentUser),
      email: currentUser.email,
      role: 'mob-pwa',
    }
    await Talk.ready
    session.value = new Talk.Session({
      appId,
      me: new Talk.User(talkjsUser),
      signature: signature.value,
    })
    try {
      if ('Notification' in window && Notification.permission === 'granted') {
        if (!$pwa?.isPWAInstalled || !isMobileOrTablet) {
          session.value.setDesktopNotificationEnabled(true, { alertOnFailure: false })
        }
      }
    }
    catch (e) {
      logger.error('Error setting desktop notifications', undefined, e as Error)
    }
  }

  const updateUnreadMessageCount = (amount: number) => {
    unreadMessageCount.value = amount
  }

  const getPrivateConversations = async (): Promise<void> => {
    const { data } = await $apiFetch<{ conversations: PrivateConversationAPIResponse[], users: CanxUser[] }>(`chat_private_conversations`, `/api/users/chat/user/conversations`)
    if (data.value?.conversations && data.value?.users) {
      privateConversations.value.conversations = data.value.conversations
      privateConversations.value.users = data.value.users
    }
  }

  const createPrivateConversation = async (currentUserID: number, otherUserID: number): Promise<string> => {
    const { data } = await $apiFetch<{ conversation: PrivateConversationAPIResponse, users: CanxUser[] }>(`get_create_chat_private_conversation`, `/api/users/chat/user/conversations/private`, { method: 'PUT', body: { participants: [otherUserID.toString()] } })
    if (data.value?.conversation && data.value?.users) {
      if (!privateConversations.value.conversations.some(c => c.id == data.value?.conversation.id)) {
        privateConversations.value.conversations.push(data.value.conversation)
      }

      const missingUsers = data.value.users.filter((u: CanxUser) => !privateConversations.value.users.includes((pcu: { id: number }) => pcu.id == u.id))
      privateConversations.value.users.push(...missingUsers)
    }

    return generatePredictableConversationID(currentUserID, otherUserID)
  }

  const setPrivateConversationMessageToSend = (message: string) => {
    privateConversationMessageToSend.value = message
  }

  const conversationExists = (userID1: number, userID2: number): boolean => {
    const pConvID = generatePredictableConversationID(userID1, userID2)
    return privateConversations.value.conversations.some(({ id }) => id === pConvID)
  }

  const getPublicConversations = async () => {
    const { data } = await $apiFetch<{ conversations: PublicConversationAPIResponse[], messages: ConversationMessageAPIResponse[] }>(`chat_public_conversations`, `/api/users/chat/conversations`)

    messages.value = data.value!.messages.filter((m: ConversationMessageAPIResponse) => !!m).map(toConversationMessage)
    conversations.value = data.value!.conversations
      .map((c: PublicConversationAPIResponse) => toPublicConversation(c, messages.value))
      .sort((a: PublicConversation, b: PublicConversation) => b.dateUpdated.getTime() - a.dateUpdated.getTime())
  }

  const setActivePublicConversation = (conversation: PublicConversation | null) => {
    activePublicConversation.value = conversation
    const talkConv = session.value?.getOrCreateConversation(conversation!.hash)
    publicChatbox.value!.select(talkConv, { asGuest: true })
  }

  const setPublicChatbox = async (chatbox: Talk.Chatbox) => {
    publicChatbox.value = chatbox
  }

  const resetPublicChatbox = () => {
    if (publicChatbox.value) {
      publicChatbox.value.destroy()
      publicChatbox.value = null
      activePublicConversation.value = null
    }
  }

  return {
    conversations, signature, session, unreadMessageCount, activePublicConversation, publicChatbox, privateConversations, privateConversationMessageToSend,
    conversationExists, buildPrivateConversation,
    getSignature, startSession, updateUnreadMessageCount, getPrivateConversations, createPrivateConversation, getPublicConversations, setActivePublicConversation, setPublicChatbox, resetPublicChatbox, setPrivateConversationMessageToSend,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useChatStore, import.meta.hot))
}
